import React, { useEffect } from 'react';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { useStore } from '../../../../stores/root';
import styles from './styles.module.scss';

// assets
import { faArrowRight, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import OrderService from '../../../../services/orders/order';

// props
import { IOrderResourceProps, IOrderResourceShortProps, IOrderResourcerShorterProps } from '../../../../props/orders/order';
import { IVendorResourceShortProps } from '../../../../props/data/vendors';
import { IOfferResourceProps } from '../../../../props/offers';

// components
import { ActionButton, Checkbox, CommandBar, DatePicker, Dropdown, IButtonProps, ICommandBarItemProps, PrimaryButton, SpinnerSize, Stack } from '@fluentui/react';
import Text from '../../../typography/text';
import LoadingComponent from '../../../feedbacks/loading';
import Label from '../../../typography/label';
import Tag, { TTagVariant } from '../../../uiframeworks/tag';
import GeneralService from '../../../../services/general';
import ErrorService from '../../../../services/general/error';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OrdersDashboardChartFilter, { SelectedFilterProps } from './filters';
import OrdersDashboardTotalOrdersChart from './graphs/totalOrders';
import OrdersDashboardIncomesOutcomesChart from './graphs/incomesOutcomes';
import DetailsStack from '../../../uiframeworks/detailsStack';
import OrdersDashboardTotalProfitChart from './graphs/totalProfit';
import OrdersList from '../../general/list';
import OrdersDashboardTotalCustomersChart from './graphs/totalCustomers';
import OrdersPerformanceSummary from './performanceSummary';
import PivotButton from '../../../uiframeworks/buttons/pivot';
import DeliveredGoodsType from '../../../../manifests/deliveredGoodsType';

type OrdersDashboardChartProps = {
}

const OrdersDashboardChart: React.FC<OrdersDashboardChartProps> = (props: OrdersDashboardChartProps) => {
    const { banner } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [groupBy, setGroupBy] = React.useState<string>("created");
    const [dateRange, setDateRange] = React.useState<string>("thisweek");
    const [selectedDate, setSelectedDate] = React.useState<{ startDate: string, endDate: string }>({
        startDate: moment().startOf('week').toISOString(),
        endDate: moment().endOf('week').toISOString()
    });
    const [orders, setOrders] = React.useState<IOrderResourceProps[]>([]);
    const [filteredOrders, setFilteredOrders] = React.useState<IOrderResourceProps[]>([]);
    const [totalOrdersBy, setTotalOrdersBy] = React.useState<string>('month');
    const [unrunLossOrders, setUnrunLossOrders] = React.useState<IOrderResourceProps[]>([]);
    const [filteredUnrunLossOrders, setFilteredUnrunLossOrders] = React.useState<IOrderResourceProps[]>([]);
    const [activeListTab, setActiveListTab] = React.useState<'all' | 'unrunLoss'>('all');
    const [selectedFilters, setSelectedFilters] = React.useState<SelectedFilterProps>({});

    useEffect(() => {
        setFilteredOrders(applyFilters(orders, selectedFilters));
        setFilteredUnrunLossOrders(applyFilters(unrunLossOrders, selectedFilters));
    }, [orders, unrunLossOrders, selectedFilters]);

    const getAllOrderVendors = (order: IOrderResourceProps) => {
        if (order.vendors) {
            return order.vendors;
        }

        const vendors: IVendorResourceShortProps[] = [];
        const addVendor = (v?: IVendorResourceShortProps) => {
            if (v && v.id && !vendors.find(existing => existing.id === v.id)) {
                vendors.push(v);
            }
        };

        (order.trucks || []).forEach(t => addVendor(t.truck?.vendor));

        if (order.offer) {
            const o = order.offer as IOfferResourceProps;
            const checkCosts = (costs?: any[]) => {
                (costs || []).forEach(c => addVendor(c.vendor));
            };
            checkCosts(o.additionalCosts);
            checkCosts(o.insuranceCosts);
            checkCosts(o.charteredShipCosts);
            checkCosts(o.originTruckingCosts);
            checkCosts(o.destinationTruckingCosts);
            checkCosts(o.loadingLabourCosts);
            checkCosts(o.unloadingLabourCosts);
            checkCosts(o.containerShippingCosts);
        }

        return vendors;
    };

    const applyFilters = (rawOrders: IOrderResourceProps[], filter: SelectedFilterProps) => {
        let result = rawOrders;

        if (filter.company && filter.company.length > 0) {
            const selectedIds = filter.company.map((company) => company.id);
            result = result.filter((order) => selectedIds.indexOf(order.company?.id || "") > -1);
        }

        if (filter.goodsType && filter.goodsType.length > 0) {
            result = result.filter((order) => (filter.goodsType || []).indexOf(order.goodsType) > -1);
        }

        if (filter.freightCategory && filter.freightCategory.length > 0) {
            const selectedIds = filter.freightCategory.map((freightCategory) => freightCategory.id);
            result = result.filter((order) => selectedIds.indexOf(order.freightCategory?.id || "") > -1);
        }

        if (filter.sales && filter.sales.length > 0) {
            const selectedIds = filter.sales.map((sales) => sales.id);
            result = result.filter((order) => {
                const exist = (order.sales || []).findIndex((s) => selectedIds.indexOf(s.sales.id) > -1) > -1;
                return exist;
            });
        }

        if (filter.status && filter.status.length > 0) {
            const selectedIds = filter.status.map((status) => status.id);
            result = result.filter((order) => selectedIds.indexOf(order.status.id || "") > -1);
        }

        if (filter.subsidiary && filter.subsidiary.length > 0) {
            const selectedIds = filter.subsidiary.map((subsidiary) => subsidiary.id);
            result = result.filter((order) => selectedIds.indexOf(order.subsidiary?.id || "") > -1);
        }

        if (filter.paymentStatus && filter.paymentStatus.length > 0) {
            result = result.filter((order) => {
                const paymentStatus = Number(order.unpaidAmount) <= 0 ? 'Paid' : 'Unpaid';
                return (filter.paymentStatus || []).indexOf(paymentStatus) > -1;
            });
        }

        if (filter.vendor && filter.vendor.length > 0) {
            const selectedIds = filter.vendor.map((vendor) => vendor.id);
            result = result.filter((order) => {
                const allVendors = getAllOrderVendors(order);
                return allVendors.some(v => selectedIds.indexOf(v.id) > -1);
            });
        }

        return result;
    }

    useEffect(() => {
        _onRetrieveOrders();
    }, [selectedDate, groupBy]);

    const _onRetrieveOrders = async () => {
        try {
            setLoaded(false);
            let groupByFilterText = 'date';

            const qs: string[] = [];
            if (groupBy === 'actualdeparture') {
                groupByFilterText = "actual_departure_date";
            } else if (groupBy === 'estimateddeparture') {
                groupByFilterText = "estimated_departure_date";
            } else if (groupBy === 'updated') {
                groupByFilterText = "date";
                qs.push(`date_basis=updated_at`);
            }

            qs.push(`start_${groupByFilterText}=${selectedDate.startDate}`);
            qs.push(`end_${groupByFilterText}=${selectedDate.endDate}`);
            qs.push(`top=all`);
            qs.push(`return_type=summary`);

            const _orders = await OrderService.retrieve(qs.join('&'));

            setOrders(_orders.data);
            setFilteredOrders(applyFilters(_orders.data, selectedFilters));

            const unrunLossQs: string[] = [];
            unrunLossQs.push(`unprofitable_unrun=1`);
            if (groupBy === 'actualdeparture') {
                unrunLossQs.push(`start_date=${selectedDate.startDate}`);
                unrunLossQs.push(`end_date=${selectedDate.endDate}`);
            } else if (groupBy === 'updated') {
                unrunLossQs.push(`date_basis=updated_at`);
                unrunLossQs.push(`start_date=${selectedDate.startDate}`);
                unrunLossQs.push(`end_date=${selectedDate.endDate}`);
            } else {
                unrunLossQs.push(`start_${groupByFilterText}=${selectedDate.startDate}`);
                unrunLossQs.push(`end_${groupByFilterText}=${selectedDate.endDate}`);
            }
            unrunLossQs.push(`top=all`);
            unrunLossQs.push(`return_type=summary`);

            const _unrunLoss = await OrderService.retrieve(unrunLossQs.join('&'));
            setUnrunLossOrders(_unrunLoss.data);
            setFilteredUnrunLossOrders(_unrunLoss.data);

            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_new_orders_error',
                text: 'Failed to retrieve new orders. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onFilterChanged = (selectedFilter: SelectedFilterProps) => {
        setSelectedFilters(selectedFilter);
    }

    const exportToCSV = () => {
        const dataToExport = activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders;

        // Headers
        const headers = [
            'Order Number',
            'Customer',
            'Company',
            'Vendor',
            'Sales',
            'Batch',
            'Freight Category',
            'Delivery Type',
            'Goods Type',
            'Pick-up Address',
            'Delivery Address',
            'Total Price',
            'Profit',
            'Status',
            'Payment Status',
            'Created At'
        ];

        const isVendorFiltered = selectedFilters.vendor && selectedFilters.vendor.length > 0;
        if (isVendorFiltered) {
            headers.push('Selected Filtered Vendor(s)');
        }

        // Rows
        const rows = dataToExport.map((item) => {
            const orderNumSplitted = item.orderNumber.split('.');
            orderNumSplitted.shift();
            orderNumSplitted.shift();
            const orderNumber = `#${orderNumSplitted.join('.')}`;
            
            const customer = item.customer?.name || '';
            const company = item.company?.name || '';
            
            const allVendors = getAllOrderVendors(item);
            const vendors = allVendors.map((v) => v.name || '').filter(Boolean).join('; ');

            const sales = (item.sales || []).map((s) => s.sales?.name || '').filter(Boolean).join('; ');
            const batch = item.batch || '';
            const freightCategory = item.freightCategory?.name || '';
            const deliveryType = item.deliveryType?.name || '';
            
            const goodsTypeObj = DeliveredGoodsType.find((t) => t.key === item.goodsType);
            const goodsType = goodsTypeObj?.text || item.goodsType || '';

            const pickupAddresses = (item.originAddresses || []).map((addr) => 
                OrderService.getAddress(addr.type, addr.address, addr.city, addr.province)
            ).join('; ');

            const deliveryAddresses = (item.destinationAddresses || []).map((addr) => 
                OrderService.getAddress(addr.type, addr.address, addr.city, addr.province)
            ).join('; ');

            const totalPrice = item.totalAmount;
            const profit = item.nettProfit;
            const status = item.status?.name || '';
            const paymentStatus = item.paymentCompleted ? 'Paid' : 'Unpaid';
            const createdAt = moment(item.createdAt).format("DD/MM/YYYY HH:mm");

            const rowData = [
                orderNumber,
                customer,
                company,
                vendors,
                sales,
                batch,
                freightCategory,
                deliveryType,
                goodsType,
                pickupAddresses,
                deliveryAddresses,
                totalPrice,
                profit,
                status,
                paymentStatus,
                createdAt
            ];

            if (isVendorFiltered) {
                const selectedVendorNames = selectedFilters.vendor?.map(v => v.name).join('; ') || '';
                rowData.push(selectedVendorNames);
            }

            return rowData.map(val => {
                if (val === undefined || val === null) return '';
                let escaped = String(val).replace(/"/g, '""');
                if (escaped.includes(',') || escaped.includes('\n') || escaped.includes('"') || escaped.includes(';')) {
                    escaped = `"${escaped}"`;
                }
                return escaped;
            });
        });

        const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `Orders_Dashboard_${activeListTab}_${moment().format('YYYYMMDD_HHmmss')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Dropdown label={'Group by'}
                options={[
                    { key: 'created', text: 'Created date' },
                    { key: 'updated', text: 'Order completion / update date' },
                    { key: 'actualdeparture', text: 'Actual departure/start date' },
                    { key: 'estimateddeparture', text: 'Estimated departure/start date' }
                ]}
                disabled={!loaded}
                styles={{ root: { width: 250 } }}
                selectedKey={groupBy}
                onChange={(ev, opt) => {
                    const _groupBy = opt?.key as string || groupBy;
                    setGroupBy(_groupBy);
                }} />
            <Dropdown label={'Date range'}
                options={[
                    { key: 'thisweek', text: 'This week' },
                    { key: 'lastweek', text: 'Last week' },
                    { key: 'thismonth', text: 'This month' },
                    { key: 'lastmonth', text: 'Last month' },
                    { key: 'last3month', text: 'Last 3 months' },
                    { key: 'thisyear', text: 'This year' },
                    { key: 'lastyear', text: 'Last year' },
                    { key: 'custom', text: 'Custom' }
                ]}
                disabled={!loaded}
                styles={{ root: { width: 200 } }}
                selectedKey={dateRange}
                onChange={(ev, opt) => {
                    let _startDate = selectedDate.startDate;
                    let _endDate = selectedDate.endDate;
                    let _dateRange = opt?.key as string || dateRange;

                    if (_dateRange === 'thismonth') {
                        _startDate = moment().startOf('month').toISOString();
                        _endDate = moment().endOf('month').toISOString();
                    } else if (_dateRange === 'lastmonth') {
                        _startDate = moment().startOf('month').add(-1, 'month').toISOString();
                        _endDate = moment().endOf('month').add(-1, 'month').toISOString();
                    } else if (_dateRange === 'last3month') {
                        _startDate = moment().startOf('month').add(-2, 'month').toISOString();
                        _endDate = moment().endOf('month').toISOString();
                    } else if (_dateRange === 'thisweek') {
                        _startDate = moment().startOf('week').toISOString();
                        _endDate = moment().endOf('week').toISOString();
                    } else if (_dateRange === 'lastweek') {
                        _startDate = moment().startOf('week').add(-1, 'week').toISOString();
                        _endDate = moment().endOf('week').add(-1, 'week').toISOString();
                    } else if (_dateRange === 'thisyear') {
                        _startDate = moment().startOf('year').toISOString();
                        _endDate = moment().endOf('year').toISOString();
                    } else if (_dateRange === 'lastyear') {
                        _startDate = moment().startOf('year').add(-1, 'year').toISOString();
                        _endDate = moment().endOf('year').add(-1, 'year').toISOString();
                    }

                    setSelectedDate({ startDate: _startDate, endDate: _endDate });
                    setDateRange(_dateRange);
                }} />
            {dateRange === 'custom' ? <Stack verticalAlign='center' horizontal tokens={{ childrenGap: 20 }} styles={{ root: { paddingTop: 26 } }}>
                <DatePicker value={moment(selectedDate.startDate).toDate()}
                    styles={{ root: { width: 150 } }}
                    disabled={!loaded}
                    formatDate={GeneralService.formatDate}
                    onSelectDate={(date) => {
                        const _startDate = date ? moment(date).startOf('day').toISOString() : selectedDate.startDate;
                        setSelectedDate({ startDate: _startDate, endDate: selectedDate.endDate });
                    }} />
                <Stack><FontAwesomeIcon icon={faArrowRight} /></Stack>
                <DatePicker value={moment(selectedDate.endDate).toDate()}
                    styles={{ root: { width: 150 } }}
                    disabled={!loaded}
                    formatDate={GeneralService.formatDate}
                    onSelectDate={(date) => {
                        const _endDate = date ? moment(date).endOf('day').toISOString() : selectedDate.endDate;
                        setSelectedDate({ startDate: selectedDate.startDate, endDate: _endDate });
                    }} />
            </Stack> : null}
        </Stack>
        <Stack className={'divider'}></Stack>
        {!loaded ? <LoadingComponent label={'Generating charts ...'} labelPosition={'right'} spinnerPosition={'baseline'} size={SpinnerSize.large} /> : null}
        {loaded ? <Stack horizontal tokens={{ childrenGap: 20 }}>
            <Stack styles={{ root: { width: 225, minWidth: 225 } }} tokens={{ childrenGap: 20 }}>
                <Stack tokens={{ childrenGap: 10 }}>
                    <Label size="small">Order type</Label>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Checkbox 
                            checked={activeListTab === 'all'} 
                            onChange={() => setActiveListTab('all')} 
                            onRenderLabel={() => (
                                <Stack styles={{ root: { marginTop: 3, marginLeft: 5 } }}>
                                    <Text size={'small'}>All Filtered Orders ({filteredOrders.length})</Text>
                                </Stack>
                            )} 
                        />
                        <Checkbox 
                            checked={activeListTab === 'unrunLoss'} 
                            onChange={() => setActiveListTab('unrunLoss')} 
                            onRenderLabel={() => (
                                <Stack styles={{ root: { marginTop: 3, marginLeft: 5 } }}>
                                    <Text size={'small'}>Unrun Loss Orders ({filteredUnrunLossOrders.length})</Text>
                                </Stack>
                            )} 
                        />
                    </Stack>
                </Stack>
                <OrdersDashboardChartFilter 
                    filteredOrders={activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders} 
                    orders={activeListTab === 'all' ? orders : unrunLossOrders} 
                    onChange={_onFilterChanged} 
                />
            </Stack>
            {(activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders).length <= 0 ? <Text>No orders found</Text> : null}
            {(activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders).length > 0 ? <Stack styles={{ root: { width: '100%' } }} tokens={{ childrenGap: 20 }}>
                <OrdersPerformanceSummary orders={activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders} />
                <Stack tokens={{ childrenGap: 20 }} className={styles.card} styles={{ root: { width: '100%', height: 450 } }}>
                    <OrdersDashboardIncomesOutcomesChart orders={activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders} groupBy={groupBy} />
                </Stack>
                <DetailsStack left={[
                    {
                        key: 'graph',
                        subsections: [
                            {
                                key: 'profitBreakdown',
                                header: { title: 'Profits Breakdown' },
                                isCard: true,
                                content: <OrdersDashboardTotalOrdersChart orders={activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders} groupBy={groupBy} />
                            },
                            {
                                key: 'customerBreakdown',
                                header: { title: 'Customers Breakdown' },
                                isCard: true,
                                content: <OrdersDashboardTotalCustomersChart orders={activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders} groupBy={groupBy} />
                            }
                        ]
                    }
                ]} />
                <DetailsStack left={[
                    {
                        key: 'orders',
                        header: { 
                            title: 'Orders List',
                            buttons: [
                                {
                                    text: 'Export to CSV',
                                    iconProps: { iconName: 'Download' },
                                    onClick: exportToCSV
                                }
                            ]
                        },
                        isCard: true,
                        content: <OrdersList orders={activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders}
                                    hideCommandBar={true}
                                    hideStatusFilter={true}
                                    columns={['orderNumber', 'customer', 'deliveryType', 'sales', 'deliveryDetails', 'price', 'profit', 'orderStatus', 'paymentStatus']}
                                    pagination={{currentPage: 1, lastPage: Math.ceil((activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders).length / 10), itemsPerPage: 10, total: (activeListTab === 'all' ? filteredOrders : filteredUnrunLossOrders).length}} />
                    }
                ]} />
            </Stack> : null}
        </Stack> : null}
    </Stack>;
};

export default OrdersDashboardChart;
